<template>
  <div class="nx-list-page payment-order-page">
    <avue-crud
      ref="crud"
      v-model="form"
      :option="option"
      :table-loading="loading"
      :data="data"
      :page.sync="page"
      :search.sync="query"
      :defaults.sync="defaults"
      @search-change="searchChange"
      @search-reset="searchReset"
      @refresh-change="refreshChange"
      @current-change="currentChange"
      @size-change="sizeChange"
      @on-load="onLoad"
    >
      <template slot="menuLeft">
        <el-button
          type="primary"
          size="small"
          plain
          icon="el-icon-download"
          :loading="exportLoading"
          @click="handleExport"
        >导出</el-button>
      </template>
      <template slot="bizNo" slot-scope="{ row }">
        <div>{{ row.bizNo || '—' }}</div>
        <div v-if="row.bizId" class="sub-line">id {{ row.bizId }}</div>
      </template>
      <template slot="custName" slot-scope="{ row }">
        <div>{{ row.custName || '—' }}</div>
        <div v-if="row.custNo" class="sub-line">{{ row.custNo }}</div>
      </template>
      <template slot="varName" slot-scope="{ row }">
        <div>{{ row.varName || '—' }}</div>
        <div v-if="row.varNo" class="sub-line">{{ row.varNo }}</div>
      </template>
      <template slot="driverName" slot-scope="{ row }">
        <div>{{ row.driverName || '—' }}</div>
        <div v-if="row.telphone" class="sub-line">{{ row.telphone }}</div>
      </template>
      <template slot="paymentStatus" slot-scope="{ row }">
        <matrix-tag :color="paymentStatusColor(row.paymentStatus)" :text="paymentStatusLabel(row.paymentStatus)" />
      </template>
      <template slot="menu" slot-scope="{ row }">
        <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
      </template>
    </avue-crud>

    <el-drawer
      title="支付订单详情"
      :visible.sync="drawerVisible"
      direction="rtl"
      size="520px"
      append-to-body
      @closed="detailRow = null"
    >
      <template v-if="detailRow">
        <dl class="detail-kv">
          <template v-for="item in detailItems">
            <dt :key="item.label + '-label'">{{ item.label }}</dt>
            <dd :key="item.label + '-value'">{{ item.value }}</dd>
          </template>
        </dl>
        <h4 class="detail-section-title">支付流水</h4>
        <el-table :data="payLogRows" border size="small" empty-text="暂无成功流水">
          <el-table-column prop="id" label="流水号" min-width="120" />
          <el-table-column prop="status" label="状态" width="80" />
          <el-table-column prop="transactionId" label="微信单号" min-width="160" show-overflow-tooltip />
          <el-table-column prop="time" label="时间" width="160" />
        </el-table>
      </template>
    </el-drawer>
  </div>
</template>

<script>
import crudMixins from '@/mixins/crud-mixins';
import matrixTag from '@/components/matrix/matrix-tag';
import { searchTimeConfig } from '@/basic-common/util/common';
import { getSettingDate } from '@/util/date';
import { getList, getDetail, exportExcel } from './api';
import { downloadXls } from '@/util/util';

const BIZ_TYPE_MAP = { 0: '其它', 1: '派车单' };
const PAYMENT_STATUS_MAP = {
  1: '未付款',
  2: '已付款',
  3: '付款失败',
  4: '已取消',
};

export default {
  name: 'finance-payment-order',
  components: { matrixTag },
  mixins: [crudMixins],
  data() {
    return {
      apiConfig: { getList, getDetail },
      exportLoading: false,
      drawerVisible: false,
      detailRow: null,
      query: {
        bizType: 1,
      },
      option: {
        ...this.$store.state.publicOption,
        addBtn: false,
        editBtn: false,
        delBtn: false,
        viewBtn: false,
        menuWidth: 90,
        labelWidth: 120,
        column: [
          {
            label: '订单ID',
            prop: 'id',
            width: 170,
            addDisplay: false,
            editDisplay: false,
          },
          {
            label: '业务类型',
            prop: 'bizType',
            type: 'select',
            search: true,
            width: 100,
            dicData: [{ label: '派车单', value: 1 }],
            formatter: (row) => BIZ_TYPE_MAP[row.bizType] || row.bizType,
          },
          {
            label: '派车单号',
            prop: 'bizNo',
            slot: true,
            search: true,
            minWidth: 150,
          },
          {
            label: '客户',
            prop: 'custName',
            slot: true,
            minWidth: 130,
          },
          {
            label: '客户',
            prop: 'custNo',
            type: 'select',
            remote: true,
            dicUrl: '/api/produce/blade-dept/select/distinct/select',
            props: {
              label: 'deptName',
              value: 'id',
            },
            search: true,
            hide: true,
            display: false,
          },
          {
            label: '煤种',
            prop: 'varName',
            slot: true,
            minWidth: 130,
          },
          {
            label: '煤种',
            prop: 'varNo',
            type: 'select',
            remote: true,
            search: true,
            hide: true,
            display: false,
            dicUrl: '/api/produce/vardict/select/distinct/pycode?varName={{key}}',
            props: {
              label: 'varName',
              value: 'varNo',
            },
          },
          {
            label: '发运类型',
            prop: 'shipType',
            type: 'select',
            search: true,
            width: 110,
            dataType: 'string',
            dicData: this.$store.getters.commonDict['businessType'],
            props: {
              label: 'dictValue',
              value: 'dictKey',
            },
            formatter: (row) => this.shipTypeLabel(row.shipType),
          },
          {
            label: '车牌号',
            prop: 'vehicleNo',
            search: true,
            width: 110,
          },
          {
            label: '司机',
            prop: 'driverName',
            slot: true,
            width: 120,
          },
          {
            label: '金额(元)',
            prop: 'paymentAmount',
            width: 100,
          },
          {
            label: '支付状态',
            prop: 'paymentStatus',
            slot: true,
            type: 'select',
            search: true,
            width: 100,
            dicData: [
              { label: '未付款', value: 1 },
              { label: '已付款', value: 2 },
              { label: '付款失败', value: 3 },
              { label: '已取消', value: 4 },
            ],
          },
          {
            label: '支付时间',
            prop: 'paymentTime',
            type: 'datetime',
            format: 'yyyy-MM-dd HH:mm:ss',
            valueFormat: 'yyyy-MM-dd HH:mm:ss',
            width: 170,
          },
          {
            ...searchTimeConfig,
            paramsKey: ['startTime', 'endTime'],
            searchValue: [getSettingDate('dayS'), getSettingDate('dayE')],
          },
        ],
      },
    };
  },
  computed: {
    detailItems() {
      const r = this.detailRow;
      if (!r) return [];
      return [
        { label: '订单ID', value: r.id },
        { label: '业务类型', value: BIZ_TYPE_MAP[r.bizType] || r.bizType },
        { label: '业务单号', value: r.bizNo || '—' },
        { label: '派车单ID', value: r.bizId || '—' },
        {
          label: '客户',
          value: r.custName ? `${r.custName}（${r.custNo || ''}）` : '—',
        },
        {
          label: '煤种',
          value: r.varName ? `${r.varName}（${r.varNo || ''}）` : '—',
        },
        { label: '发运类型', value: this.shipTypeLabel(r.shipType) },
        { label: '车牌', value: r.vehicleNo || '—' },
        {
          label: '司机',
          value: [r.driverName, r.telphone].filter(Boolean).join(' / ') || '—',
        },
        { label: '金额', value: r.paymentAmount != null ? `¥${r.paymentAmount}` : '—' },
        { label: '支付状态', value: this.paymentStatusLabel(r.paymentStatus) },
        { label: '微信交易号', value: r.transactionId || '—' },
        { label: '支付时间', value: r.paymentTime || '—' },
        { label: '创建时间', value: r.createTime || '—' },
      ];
    },
    payLogRows() {
      const r = this.detailRow;
      if (!r || r.paymentStatus !== 2) return [];
      return [
        {
          id: `PL${r.id}`,
          status: '成功',
          transactionId: r.transactionId || '—',
          time: r.paymentTime || '—',
        },
      ];
    },
  },
  methods: {
    shipTypeLabel(val) {
      const list = this.$store.getters.commonDict['businessType'] || [];
      const hit = list.find((d) => String(d.dictKey) === String(val));
      return hit ? hit.dictValue : val;
    },
    paymentStatusLabel(status) {
      return PAYMENT_STATUS_MAP[status] || status;
    },
    paymentStatusColor(status) {
      const map = { 1: '#d46b08', 2: '#009a29', 3: '#cb2634', 4: '#4e5969' };
      return map[status] || '#4e5969';
    },
    openDetail(row) {
      getDetail(row.id).then((res) => {
        this.detailRow = res.data.data || res.data;
        this.drawerVisible = true;
      });
    },
    async handleExport() {
      const total = this.page.total || 0;
      if (total < 1) {
        this.$message.warning('无数据可导出');
        return;
      }
      const maxRows = 10000;
      if (total > maxRows) {
        this.$message.warning(`当前最多导出 ${maxRows} 条，请缩小筛选范围后重试`);
        return;
      }
      this.exportLoading = true;
      try {
        const params = this.formatParams(Object.assign({}, this.query));
        const res = await exportExcel(params);
        downloadXls(res.data, `支付订单_${Date.now()}.xlsx`);
        this.$message.success('导出成功');
      } catch (e) {
        console.warn(e);
        this.$message.error((e && e.message) || '导出失败');
      } finally {
        this.exportLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.sub-line {
  font-size: 12px;
  color: #86909c;
  line-height: 1.4;
}
.detail-kv {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px 12px;
  margin: 0 0 16px;
  font-size: 13px;
  dt {
    margin: 0;
    color: #86909c;
  }
  dd {
    margin: 0;
    word-break: break-all;
  }
}
.detail-section-title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
}
</style>
